<script setup lang="ts" generic="T extends { id: string | number }">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    items: T[];
    itemsPerPage?: number;
    ariaLabel?: string;
  }>(),
  {
    itemsPerPage: 4,
    ariaLabel: 'Carrousel',
  },
);

const scrollerRef = useTemplateRef<HTMLElement>('scroller');
const pageRefs = ref<HTMLElement[]>([]);
const currentPageIndex = ref(0);
const pageHeight = ref<number | null>(null);

const pages = computed(() => {
  const chunks: T[][] = [];
  for (let i = 0; i < props.items.length; i += props.itemsPerPage) {
    chunks.push(props.items.slice(i, i + props.itemsPerPage));
  }
  return chunks;
});

const pageCount = computed(() => pages.value.length);
const canGoPrev = computed(() => currentPageIndex.value > 0);
const canGoNext = computed(() => currentPageIndex.value < pageCount.value - 1);

const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
  : false;

let intersectionObserver: IntersectionObserver | null = null;
let resizeObserver: ResizeObserver | null = null;

function setPageRef(el: Element | null, index: number) {
  if (el instanceof HTMLElement) {
    pageRefs.value[index] = el;
  }
}

function scrollToPage(index: number) {
  const target = pageRefs.value[index];
  if (!target || !scrollerRef.value)
    return;
  scrollerRef.value.scrollTo({
    top: target.offsetTop,
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
  });
}

function goPrevPage() {
  if (canGoPrev.value)
    scrollToPage(currentPageIndex.value - 1);
}

function goNextPage() {
  if (canGoNext.value)
    scrollToPage(currentPageIndex.value + 1);
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    goNextPage();
  }
  else if (event.key === 'ArrowUp') {
    event.preventDefault();
    goPrevPage();
  }
}

function measurePageHeight() {
  const first = pageRefs.value[0];
  if (first)
    pageHeight.value = first.getBoundingClientRect().height;
}

function setupObservers() {
  if (!scrollerRef.value)
    return;

  intersectionObserver?.disconnect();
  intersectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const index = pageRefs.value.indexOf(entry.target as HTMLElement);
          if (index !== -1)
            currentPageIndex.value = index;
        }
      }
    },
    { root: scrollerRef.value, threshold: 0.6 },
  );
  pageRefs.value.forEach(page => intersectionObserver!.observe(page));

  resizeObserver?.disconnect();
  if (pageRefs.value[0]) {
    resizeObserver = new ResizeObserver(() => measurePageHeight());
    resizeObserver.observe(pageRefs.value[0]);
  }
}

onMounted(async () => {
  await nextTick();
  measurePageHeight();
  setupObservers();
});

watch(pages, async () => {
  currentPageIndex.value = 0;
  pageRefs.value = [];
  pageHeight.value = null;
  await nextTick();
  measurePageHeight();
  setupObservers();
});

onBeforeUnmount(() => {
  intersectionObserver?.disconnect();
  resizeObserver?.disconnect();
});

defineExpose({ goPrevPage, goNextPage });
</script>

<template>
  <section class="carrousel-list yb-preset" role="region" aria-roledescription="carrousel" :aria-label="ariaLabel">
    <div class="carrousel-list_viewport">
      <ol
        ref="scroller"
        class="carrousel-list_scroller"
        :style="pageHeight ? { height: `${pageHeight}px` } : undefined"
        :aria-label="ariaLabel"
        tabindex="0"
        @keydown="handleKeydown"
      >
        <li
          v-for="(page, pageIndex) in pages"
          :key="pageIndex"
          :ref="el => setPageRef(el as Element | null, pageIndex)"
          class="carrousel-list_page"
          role="group"
          aria-roledescription="slide"
          :aria-label="`Page ${pageIndex + 1} sur ${pageCount}`"
        >
          <div
            v-for="item in page"
            :key="item.id"
            class="carrousel-list_item"
          >
            <slot name="item" :item="item" />
          </div>
        </li>
      </ol>

      <button
        v-if="pageCount > 1"
        type="button"
        class="carrousel-list_jump carrousel-list_jump--top"
        :disabled="!canGoPrev"
        aria-label="Page précédente"
        @click="goPrevPage"
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M10 15.5V4.5M10 4.5L4.5 10M10 4.5L15.5 10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <button
        v-if="pageCount > 1"
        type="button"
        class="carrousel-list_jump carrousel-list_jump--bottom"
        :disabled="!canGoNext"
        aria-label="Page suivante"
        @click="goNextPage"
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M10 4.5V15.5M10 15.5L4.5 10M10 15.5L15.5 10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>

    <p class="carrousel-list_sr-status" aria-live="polite">
      Page {{ currentPageIndex + 1 }} sur {{ pageCount }}
    </p>
  </section>
</template>

<style lang="css" scoped>
.carrousel-list {
  width: 100%;
  font-family: inherit;
}

.carrousel-list_viewport {
  position: relative;
  width: 100%;
}

.carrousel-list_scroller {
  all: unset;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  scroll-behavior: smooth;
  touch-action: pan-y;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid var(--yb-outline);
    outline-offset: -2px;
  }
}

.carrousel-list_page {
  flex: 0 0 100%;
  box-sizing: border-box;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm, 1rem);
  /* Half the card gap on each edge: last/first card of adjacent pages stay evenly spaced,
     and the spacing is baked into the measured/snapped page box instead of sitting between
     pages, so it doesn't flash as bare space while swiping on touch devices. */
  padding-block: calc(var(--yb-spacing-sm, 1rem) / 2);
}

.carrousel-list_item {
  width: 100%;
  box-sizing: border-box;
}

.carrousel-list_jump {
  all: unset;
  font-family: inherit;
  position: absolute;
  right: 0.75rem;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  background-color: var(--yb-color-primary, #111);
  color: var(--yb-color-white);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: default;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid var(--yb-outline);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: no-preference) {
    transition:
      opacity 0.2s ease-out,
      scale 0.2s ease-out;

    &:hover {
      scale: 1.05;
    }
  }

  /* Mobile and tablet navigate by touch/scroll; only show jump buttons on desktop. */
  @media screen and (width < 1024px) {
    display: none;
  }
}

.carrousel-list_jump--top {
  top: 0.75rem;
}

.carrousel-list_jump--bottom {
  bottom: 0.75rem;
}

.carrousel-list_sr-status {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
